// Pure on purpose: proxy.ts imports this, so no next/* or server-only deps.

export type HostKind = { kind: "main" } | { kind: "subdomain"; slug: string } | { kind: "foreign" };

type HostEnv = { mainHost: string; base: string };

// NEXT_PUBLIC_* must be read as literal property accesses to be inlined.
// Production has no fallback: next.config refuses to build without both.
function defaultEnv(): HostEnv {
  const fallback = process.env.NODE_ENV === "production" ? "" : "localhost:3000";
  return {
    mainHost: (process.env.NEXT_PUBLIC_ADMIN_HOST || fallback).toLowerCase(),
    base: (process.env.NEXT_PUBLIC_ADMIN_SUBDOMAIN_BASE || fallback).toLowerCase(),
  };
}

export function parseHost(host: string | null, env: HostEnv = defaultEnv()): HostKind {
  if (!host) return { kind: "foreign" };
  const h = host.toLowerCase();
  if (h === env.mainHost.toLowerCase()) return { kind: "main" };
  const suffix = `.${env.base.toLowerCase()}`;
  if (h.endsWith(suffix)) {
    const slug = h.slice(0, -suffix.length);
    if (slug && !slug.includes(".")) return { kind: "subdomain", slug };
  }
  return { kind: "foreign" };
}

export function originForSubdomain(
  slug: string | null,
  protocol: string,
  env: HostEnv = defaultEnv(),
): string {
  return `${protocol.replace(/:$/, "")}://${slug ? `${slug}.${env.base}` : env.mainHost}`;
}

// Hosts share one cookie jar, so a cross-host switch must not replace this tab's session.
export function isCrossHost(
  targetSlug: string | null,
  location: { protocol: string; origin: string },
  env: HostEnv = defaultEnv(),
): boolean {
  return originForSubdomain(targetSlug, location.protocol, env) !== location.origin;
}

export type TokenClaims = {
  customerId: string | null;
  appId: string | null;
  subdomain: string | null;
};

const str = (v: unknown) => (typeof v === "string" && v ? v : null);

export function readTokenClaims(token: string): TokenClaims {
  try {
    const b64 = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    const payload = JSON.parse(atob(b64.padEnd(Math.ceil(b64.length / 4) * 4, "=")));
    return {
      customerId: str(payload.pylo_customer_id),
      appId: str(payload.pylo_app_id),
      subdomain: str(payload.pylo_customer_subdomain),
    };
  } catch {
    return { customerId: null, appId: null, subdomain: null };
  }
}

export type GuardDecision = "continue" | "relogin" | "handoff";

export function guardDecision(
  host: HostKind,
  hostCustomerId: string | null,
  claims: TokenClaims,
): GuardDecision {
  if (host.kind === "subdomain") {
    return hostCustomerId !== null && claims.customerId === hostCustomerId ? "continue" : "relogin";
  }
  if (host.kind === "main" && claims.subdomain) return "handoff";
  return "continue";
}
